import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Input, InputNumber,
  Row, Segmented, Space, Statistic, Switch, Table, Tag, Typography,
  Tooltip, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined,
  InfoCircleOutlined, EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import {
  AGING_BUCKETS_DETAILED, agingSeverityForBucket,
  formatAgingAmount, pickPartyDisplayName, pickPartySubtitle,
  buildAgingQuery, exportAgingCsvRows, friendlyAgingBasisMessage,
  computeAgingSummary, normalizeAgingRows
} from './agingReportHelpers';
import AgingDetailDrawer from './AgingDetailDrawer';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Shared aging report shell (Stage 6E.3).
//
// Both DebtorsAgingPage and CreditorsAgingPage render this component
// with the right `reportType` + copy. Keeps the two pages identical
// in behaviour (the only meaningful differences are wording and the
// API call) and avoids two parallel maintenance trees.
// ─────────────────────────────────────────────────────────────────

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const head = headers.map(csvSafe).join(',');
  const body = rows.map((r) => headers.map((h) => csvSafe(r[h])).join(',')).join('\n');
  const blob = new Blob([head + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// dayjs-free YYYY-MM-DD from a Date.
function todayIso() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function AgingReportShell({
  reportType,           // 'debtors' | 'creditors'
  title,                // page heading
  subtitle,             // small grey line under the heading
  partyColumnTitle,     // 'Customer / Ledger' | 'Supplier / Ledger'
  emptyContextKey,      // 'debtors_aging' | 'creditors_aging'
  errorContextKey,      // 'debtors_aging' | 'creditors_aging'
  totalCardLabel        // 'Total Receivables' | 'Total Payables'
}) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Filter state.
  const [asOfDate, setAsOfDate]               = useState(todayIso());
  const [q, setQ]                             = useState('');
  const [bucketMode, setBucketMode]           = useState('detailed');
  const [includeZero, setIncludeZero]         = useState(false);
  const [minOutstanding, setMinOutstanding]   = useState(null);

  // Detail drawer.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow,  setDetailRow]  = useState(null);

  const fetcher = useMemo(() => (
    reportType === 'creditors'
      ? accountReportsApi.creditorsAging
      : accountReportsApi.debtorsAging
  ), [reportType]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const out = await fetcher(buildAgingQuery({
        asOfDate, q: q || null, bucketMode, includeZero,
        minOutstanding: (minOutstanding != null ? Number(minOutstanding) : null)
      }));
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, errorContextKey));
    } finally { setLoading(false); }
  }, [fetcher, asOfDate, q, bucketMode, includeZero, minOutstanding, errorContextKey]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = useMemo(() => normalizeAgingRows(data?.rows), [data]);
  const summary = data?.summary || computeAgingSummary([]);

  const onOpenDetail = useCallback((row) => {
    setDetailRow(row);
    setDetailOpen(true);
  }, []);

  // Column set. We render all 7 buckets in detailed mode and 5 in
  // simple mode. The Party / Ledger column is always sticky-ish wide.
  const columns = useMemo(() => {
    const cols = [
      { title: partyColumnTitle, key: 'party', fixed: 'left', width: 240,
        render: (_, r) => (
          <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
            <Text strong style={{ fontSize: 13 }}>{pickPartyDisplayName(r)}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{pickPartySubtitle(r)}</Text>
          </Space>
        ) },
      { title: 'Total (₹)', dataIndex: 'totalOutstanding', key: 'totalOutstanding',
        width: 130, align: 'right', sorter: (a, b) => a.totalOutstanding - b.totalOutstanding,
        defaultSortOrder: 'descend',
        render: (v) => <strong>{formatAgingAmount(v, { alwaysShow: true })}</strong> }
    ];

    if (bucketMode === 'simple') {
      // Compute the 5-column simple view from the detailed row fields.
      cols.push(
        { title: 'Not Due', key: 'notDue', width: 100, align: 'right',
          render: (_, r) => formatAgingAmount(r.notDue) },
        { title: '0–30',    key: 'b0_30',   width: 100, align: 'right',
          render: (_, r) => (
            <Tag color={agingSeverityForBucket('bucket0_30')} style={{ marginInlineEnd: 0 }}>
              {formatAgingAmount(r.bucket0_30)}
            </Tag>
          ) },
        { title: '31–60',   key: 'b31_60',  width: 100, align: 'right',
          render: (_, r) => (
            <Tag color={agingSeverityForBucket('bucket31_60')} style={{ marginInlineEnd: 0 }}>
              {formatAgingAmount(r.bucket31_60)}
            </Tag>
          ) },
        { title: '61–90',   key: 'b61_90',  width: 100, align: 'right',
          render: (_, r) => (
            <Tag color={agingSeverityForBucket('bucket61_90')} style={{ marginInlineEnd: 0 }}>
              {formatAgingAmount(r.bucket61_90)}
            </Tag>
          ) },
        { title: '90+',     key: 'b90Plus', width: 110, align: 'right',
          render: (_, r) => {
            const v = (r.bucket91_180 || 0) + (r.bucket181_365 || 0) + (r.bucket365Plus || 0);
            return (
              <Tag color={agingSeverityForBucket('bucket90Plus')} style={{ marginInlineEnd: 0 }}>
                {formatAgingAmount(v)}
              </Tag>
            );
          } }
      );
    } else {
      // 7 detailed buckets.
      for (const bk of AGING_BUCKETS_DETAILED) {
        cols.push({
          title: bk.label, dataIndex: bk.key, key: bk.key,
          width: bk.key === 'notDue' ? 100 : 110, align: 'right',
          render: (v) => bk.key === 'notDue'
            ? formatAgingAmount(v)
            : (
              <Tag color={agingSeverityForBucket(bk.key)} style={{ marginInlineEnd: 0 }}>
                {formatAgingAmount(v)}
              </Tag>
            )
        });
      }
    }

    cols.push(
      { title: 'Oldest Open', dataIndex: 'oldestOpenDate', key: 'oldestOpenDate',
        width: 110,
        render: (v) => v ? String(v).slice(0, 10) : <Text type="secondary">—</Text> },
      { title: 'Last Payment', dataIndex: 'lastPaymentDate', key: 'lastPaymentDate',
        width: 110,
        render: (v) => v ? String(v).slice(0, 10) : <Text type="secondary">—</Text> },
      { title: '', key: 'actions', width: 60, fixed: 'right', align: 'center',
        render: (_, r) => (
          <Tooltip title="View detail">
            <Button size="small" type="text" icon={<EyeOutlined />}
              onClick={() => onOpenDetail(r)} />
          </Tooltip>
        ) }
    );

    return cols;
  }, [bucketMode, partyColumnTitle, onOpenDetail]);

  const exportCsv = useCallback(() => {
    const filename = `${reportType}-aging-${asOfDate || todayIso()}.csv`;
    downloadCsv(filename, exportAgingCsvRows(rows, { mode: bucketMode }));
  }, [reportType, asOfDate, rows, bucketMode]);

  const oldestBucketAmount = summary.bucket365Plus || 0;

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport} loading={loading}>
              Refresh
            </Button>
            <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>
              Print
            </Button>
            <Button size="small" icon={<DownloadOutlined />} disabled={!rows.length}
              onClick={exportCsv}>
              Export CSV
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{subtitle}</div>

        {loadError ? (
          <Alert
            type="error" showIcon
            style={{ marginBottom: 10 }}
            message={`Could not load ${title}`}
            description={friendlyAccountsErrorMessage(loadError, errorContextKey)}
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>Retry</Button>
            }
          />
        ) : null}

        {/* Foundation notice — invisible once an invoice-level basis ships. */}
        {data?.meta && !data.meta.invoiceLevelAvailable ? (
          <Alert
            type="info" showIcon
            style={{ marginBottom: 10 }}
            icon={<InfoCircleOutlined />}
            message={friendlyAgingBasisMessage(data.meta)}
          />
        ) : null}

        {/* Summary cards. */}
        <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title={`${totalCardLabel} (₹)`}
                value={summary.totalOutstanding} precision={2}
                valueStyle={{
                  color: summary.totalOutstanding > 0
                    ? (reportType === 'debtors' ? '#cf1322' : '#0958d9')
                    : '#3f8600'
                }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Not Due (₹)" value={summary.notDue} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="0–30 days (₹)" value={summary.bucket0_30} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="31–60 days (₹)" value={summary.bucket31_60} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="61–90 days (₹)" value={summary.bucket61_90} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="91–180 days (₹)" value={summary.bucket91_180} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="365+ days (₹)" value={oldestBucketAmount}
                precision={2} valueStyle={{ color: oldestBucketAmount > 0 ? '#cf1322' : '#0f172a' }} />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <div style={{ fontSize: 11.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.2, fontWeight: 500 }}>
                Overdue Parties
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4, color: summary.overduePartyCount > 0 ? '#cf1322' : '#3f8600' }}>
                {summary.overduePartyCount}
              </div>
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                of {summary.partyCount} total
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters. */}
        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            <Space>
              <Text>As of:</Text>
              <DatePicker size="small"
                value={asOfDate ? dayjsLikeFromIso(asOfDate) : null}
                onChange={(_, str) => setAsOfDate(str || todayIso())}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Text>Search:</Text>
              <Input size="small" allowClear style={{ width: 180 }}
                placeholder={reportType === 'debtors' ? 'Customer / ledger' : 'Supplier / ledger'}
                value={q} onChange={(e) => setQ(e.target.value)}
                onPressEnter={fetchReport}
              />
            </Space>
            <Space>
              <Text>View:</Text>
              <Segmented size="small"
                value={bucketMode}
                onChange={setBucketMode}
                options={[
                  { value: 'detailed', label: 'Detailed' },
                  { value: 'simple',   label: 'Simple' }
                ]}
              />
            </Space>
            <Space>
              <Text>Min outstanding (₹):</Text>
              <InputNumber size="small" min={0} placeholder="0"
                value={minOutstanding}
                onChange={(v) => setMinOutstanding(v)}
                style={{ width: 120 }}
              />
            </Space>
            <Space>
              <Text>Include zero balances:</Text>
              <Switch size="small" checked={includeZero} onChange={setIncludeZero} />
            </Space>
          </Space>
        </Card>

        <Table
          size="small"
          rowKey={(r) => r.ledgerAccountId || r.partyId || `${r.ledgerCode}-${r.partyCode}`}
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 50, size: 'small', showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100', '200'] }}
          locale={{ emptyText: <Empty description={friendlyAccountsEmptyMessage(emptyContextKey)} /> }}
          onRow={(record) => ({
            onDoubleClick: () => onOpenDetail(record)
          })}
        />

        <AgingDetailDrawer
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          reportType={reportType}
          row={detailRow}
          asOfDate={asOfDate}
        />
      </div>
    </ErpMasterShell>
  );
}

// AntD DatePicker wants a dayjs-like object.
function dayjsLikeFromIso(iso) {
  return iso ? dayjs(iso) : null;
}
